/**
 * BitacoraPage — Lista de visitas registradas.
 * Mobile-first: cards. Desktop: tabla compacta.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip, Paper,
  Stack, TextField, Fab, Tooltip, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventIcon from '@mui/icons-material/Event';
import VisitaForm from './VisitaForm';
import {
  API_BASE, getEmpresaId, getAuthHeaders, fechaRelativa, formatFechaCorta,
  RESULTADOS_VISITA, CANALES_VISITA,
} from './format';

function ResultadoChip({ resultado }) {
  const meta = RESULTADOS_VISITA.find(r => r.value === resultado) || RESULTADOS_VISITA[0];
  return <Chip size="small" label={meta.label}
    sx={{ bgcolor: `${meta.color}20`, color: meta.color, fontWeight: 700, fontSize: 10, height: 18 }} />;
}

export default function BitacoraPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [filtroResultado, setFiltroResultado] = useState('');
  const [filtroCanal, setFiltroCanal] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/comercial/visitas?empresa_id=${getEmpresaId()}&limit=200`,
        { headers: getAuthHeaders() }
      );
      const d = await r.json();
      setItems(d.visitas || []);
    } catch (e) { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = items.filter(v => {
    if (filtroResultado && v.resultado !== filtroResultado) return false;
    if (filtroCanal && v.canal !== filtroCanal) return false;
    return true;
  });

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Bitácora de visitas</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Registra cada contacto en campo o en oficina.
          </Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} disableElevation
          onClick={() => setFormOpen(true)}
          sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
          Nueva visita
        </Button>
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Resultado" sx={{ minWidth: 160 }}
          value={filtroResultado} onChange={e => setFiltroResultado(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {RESULTADOS_VISITA.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Canal" sx={{ minWidth: 160 }}
          value={filtroCanal} onChange={e => setFiltroCanal(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {CANALES_VISITA.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ color: '#0d9488' }} /></Box>
      ) : filtradas.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <HistoryIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Aún no hay visitas registradas</Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5, mb: 2 }}>
            Empieza registrando tu primera visita del día.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} disableElevation
            onClick={() => setFormOpen(true)}
            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            Nueva visita
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {filtradas.map(v => (
            <Paper key={v.id} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                    {v.agencia_id
                      ? <BusinessIcon sx={{ fontSize: 16, color: '#0ea5e9' }} />
                      : <PersonSearchIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />}
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {v.agencia_nombre || v.prospecto_nombre || '—'}
                    </Typography>
                    <ResultadoChip resultado={v.resultado} />
                    {v.gps_lat && (
                      <Tooltip title={`GPS: ${v.gps_lat.toFixed(5)}, ${v.gps_lng.toFixed(5)}`}>
                        <LocationOnIcon sx={{ fontSize: 14, color: '#0d9488' }} />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                    {v.persona_contactada && `${v.persona_contactada}${v.cargo_persona ? ` (${v.cargo_persona})` : ''} · `}
                    {(CANALES_VISITA.find(c => c.value === v.canal) || {}).label || v.canal}
                    {v.duracion_minutos && ` · ${v.duracion_minutos} min`}
                  </Typography>
                  {v.notas && (
                    <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.5,
                                       display: '-webkit-box', WebkitLineClamp: 2,
                                       WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {v.notas}
                    </Typography>
                  )}
                  {v.siguiente_accion && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                      <EventIcon sx={{ fontSize: 12, color: '#f59e0b' }} />
                      <Typography sx={{ fontSize: 11, color: '#92400e', fontWeight: 600 }}>
                        {v.siguiente_accion}
                        {v.siguiente_accion_fecha && ` · ${formatFechaCorta(v.siguiente_accion_fecha)}`}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 120 }}>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                    {fechaRelativa(v.fecha_visita)}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                    {v.comercial_nombre}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* FAB en móvil — posicionado arriba del NaraChat. */}
      <Tooltip title="Nueva visita" placement="left">
        <Fab onClick={() => setFormOpen(true)}
          sx={{
            position: 'fixed', bottom: 96, right: 24, zIndex: 1301,
            display: { xs: 'flex', md: 'none' },
            background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
            color: '#fff', boxShadow: '0 8px 24px rgba(13, 148, 136, 0.4)',
          }}>
          <AddIcon />
        </Fab>
      </Tooltip>

      {formOpen && (
        <VisitaForm open={formOpen} onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); cargar(); }} />
      )}
    </Box>
  );
}
